import { useCallback, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { GripVertical } from 'lucide-react';
import { RolagemHorizontal } from './base';
import {
  DndContext,
  DragOverlay,
  KeyboardCode,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  rectIntersection,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type Announcements,
  type CollisionDetection,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
  type KeyboardCoordinateGetter,
} from '@dnd-kit/core';

export interface ColunaKanban {
  id: string;
  titulo: string;
  rodape?: ReactNode;
  /** CRM no novo desenho (lote 3): conteúdo logo abaixo do cabeçalho (as estatísticas da etapa) e ações do cabeçalho. */
  topo?: ReactNode;
  acoes?: ReactNode;
  /** Total real da coluna quando o quadro mostra só parte dos cartões (sem ele, os cartões da coluna). */
  total?: number;
  /** Texto curto ao lado do total (a soma do valor da etapa). */
  resumo?: ReactNode;
}

/** U13: a alça da coluna arrasta com o id prefixado (o id puro da coluna é o alvo dos cartões). */
const PREFIXO_COLUNA = 'coluna:';
const idDaColuna = (id: string | number) => (String(id).startsWith(PREFIXO_COLUNA) ? String(id).slice(PREFIXO_COLUNA.length) : null);

/** Lista de 30/10 (B), item 31: movimento reduzido sem a animação de soltar nem a rolagem suave do teclado. */
const movimentoReduzido = () => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/** Item 31: o ponteiro decide a coluna (o cartão pode ser largo); sem ponteiro (teclado), a interseção do retângulo. */
const colisao: CollisionDetection = (args) => {
  const sob = pointerWithin(args);
  return sob.length ? sob : rectIntersection(args);
};

/**
 * Item 31: setas para os lados levam o cartão (ou a coluna) direto ao centro da coluna vizinha, em vez de 25 px por toque;
 * para cima e para baixo seguem os 25 px de antes. Os alvos são só as colunas (o id da coluna é o alvo).
 */
const passoDoTeclado: KeyboardCoordinateGetter = (evento, { context, currentCoordinates }) => {
  const passo = 25;
  const { collisionRect, droppableRects, droppableContainers, over } = context;
  if (evento.code === KeyboardCode.Down) return { ...currentCoordinates, y: currentCoordinates.y + passo };
  if (evento.code === KeyboardCode.Up) return { ...currentCoordinates, y: currentCoordinates.y - passo };
  const lado = evento.code === KeyboardCode.Right ? 1 : evento.code === KeyboardCode.Left ? -1 : 0;
  if (!lado) return undefined;
  evento.preventDefault();
  const largura = collisionRect?.width ?? 0;
  const colunas = droppableContainers
    .getEnabled()
    .map((c) => ({ id: c.id, rect: droppableRects.get(c.id) }))
    .filter((c): c is { id: string | number; rect: NonNullable<typeof c.rect> } => !!c.rect)
    .sort((a, b) => a.rect.left - b.rect.left);
  const centro = currentCoordinates.x + largura / 2;
  const atual = over ? colunas.findIndex((c) => c.id === over.id) : -1;
  const vizinha =
    atual >= 0
      ? colunas[atual + lado]
      : lado > 0
        ? colunas.find((c) => c.rect.left + c.rect.width / 2 > centro + 1)
        : [...colunas].reverse().find((c) => c.rect.left + c.rect.width / 2 < centro - 1);
  if (!vizinha) return undefined;
  return { x: vizinha.rect.left + (vizinha.rect.width - largura) / 2, y: currentCoordinates.y };
};

function Coluna({
  coluna,
  children,
  total,
  arrastavel,
  aoMoverPeloTeclado,
  contagemNoTitulo,
  naAlturaDaJanela,
  reserva,
  soltura,
}: {
  coluna: ColunaKanban;
  children: ReactNode;
  total: number;
  /** Item 31: altura do espaço reservado no fim da coluna enquanto um cartão de outra coluna paira sobre ela. */
  reserva?: number;
  /** Review rlb-2 (VIS-B1): a coluna em que o cartão foi solto no fim do arraste (nula: cancelado ou solto em outra). */
  soltura: () => string | null;
  arrastavel: boolean;
  contagemNoTitulo?: boolean;
  naAlturaDaJanela?: boolean;
  /** Alt com seta: muda a coluna de lugar direto (-1 esquerda, +1 direita). */
  aoMoverPeloTeclado: (passo: -1 | 1) => void;
}) {
  const { t } = useTranslation();
  const { setNodeRef: alvo, isOver } = useDroppable({ id: coluna.id });
  const arrasto = useDraggable({
    id: `${PREFIXO_COLUNA}${coluna.id}`,
    disabled: !arrastavel,
    attributes: { roleDescription: t('kanban.colunaPapel') },
  });
  const estilo = arrasto.transform ? { transform: `translate3d(${arrasto.transform.x}px, 0, 0)` } : undefined;
  // Review rlb-1 (VIS-B1): a reserva entra no fim do corpo; com a coluna que rola, ela ficava fora da vista. Ao aparecer, o
  // corpo da coluna (só ele) rola até ela.
  const corpo = useRef<HTMLDivElement>(null);
  const espaco = useRef<HTMLDivElement>(null);
  const comReserva = reserva !== undefined;
  // Review rlb-2 (VIS-B1): quando a reserva sai (o cartão passou adiante, Esc, cancelou ou foi solto em outra coluna), o corpo
  // volta à rolagem de antes; só a coluna em que o cartão foi solto fica onde está (o cartão novo aparece no fim, à vista).
  useLayoutEffect(() => {
    if (!comReserva || !corpo.current || !espaco.current) return;
    const devolver = rolarAteVerGuardando(corpo.current, espaco.current);
    return () => devolver(soltura() === coluna.id);
  }, [comReserva, soltura, coluna.id]);
  return (
    <section
      ref={(no) => {
        alvo(no);
        arrasto.setNodeRef(no);
      }}
      style={estilo}
      aria-label={coluna.titulo}
      className={`flex w-72 shrink-0 flex-col rounded-xl border bg-sup-1 transition-colors ${naAlturaDaJanela ? 'altura-coluna-kanban' : ''} ${arrasto.isDragging ? 'z-20 opacity-80 shadow-xl' : ''} ${isOver && !arrasto.isDragging ? 'border-acento-forte' : 'border-borda'}`}
    >
      {/* rl0411-2 (VIS-M4): título e resumo que não cabem lado a lado quebram a linha, sem cortar nome nem o valor da segunda moeda. */}
      <header className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 border-b border-borda px-3 py-2.5">
        <div className="flex min-w-0 items-center gap-1">
          {arrastavel && (
            <button
              type="button"
              ref={arrasto.setActivatorNodeRef}
              {...arrasto.listeners}
              {...arrasto.attributes}
              aria-label={t('kanban.moverColuna', { coluna: coluna.titulo })}
              title={t('kanban.moverColuna', { coluna: coluna.titulo })}
              onKeyDown={(e: KeyboardEvent<HTMLButtonElement>) => {
                if (e.altKey && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
                  e.preventDefault();
                  return aoMoverPeloTeclado(e.key === 'ArrowLeft' ? -1 : 1);
                }
                arrasto.listeners?.onKeyDown?.(e);
              }}
              className="-my-0.5 -mr-0.5 -ml-1.5 shrink-0 cursor-grab rounded p-1 text-tinta-3 hover:text-tinta-1 active:cursor-grabbing"
            >
              <GripVertical className="size-4" aria-hidden />
            </button>
          )}
          <h3 className="truncate text-sm font-semibold" title={coluna.titulo}>
            {coluna.titulo}
            {contagemNoTitulo && <span className="numero font-normal text-tinta-3"> ({coluna.total ?? total})</span>}
          </h3>
        </div>
        <span className="flex min-w-0 items-center gap-1">
          {!contagemNoTitulo && <span className="numero text-xs text-tinta-3">{coluna.total ?? total}</span>}
          {coluna.resumo &&
            (contagemNoTitulo ? (
              <span className="numero min-w-0 truncate rounded-full bg-sup-3 px-2 py-0.5 text-xs font-medium text-tinta-2" title={typeof coluna.resumo === 'string' ? coluna.resumo : undefined} data-resumo-coluna="">
                {coluna.resumo}
              </span>
            ) : (
              <span className="numero text-xs text-tinta-2">· {coluna.resumo}</span>
            ))}
          {coluna.acoes}
        </span>
      </header>
      {coluna.topo}
      <div ref={corpo} className={`flex flex-1 flex-col gap-2 p-2 ${naAlturaDaJanela ? 'min-h-0 overflow-y-auto' : 'min-h-24'}`}>
        {children}
        {reserva !== undefined && (
          <div ref={espaco} aria-hidden data-kanban-reserva="" style={{ height: reserva }} className="shrink-0 rounded-lg border-2 border-dashed border-acento-forte bg-acento-suave" />
        )}
      </div>
      {coluna.rodape && <footer className="border-t border-borda px-3 py-2 text-xs text-tinta-3">{coluna.rodape}</footer>}
    </section>
  );
}

/**
 * Review rlb-1 (VIS-B1): rola só o `corpo` (a coluna que rola) até o `alvo` ficar à vista, pelo menor caminho; sem mexer na
 * página nem na rolagem lateral (o `scrollIntoView` rolaria também os de fora). Exportada para o teste.
 */
export function rolarAteVer(corpo: Pick<HTMLElement, 'getBoundingClientRect' | 'scrollTop'>, alvo: Pick<HTMLElement, 'getBoundingClientRect'>) {
  const c = corpo.getBoundingClientRect();
  const a = alvo.getBoundingClientRect();
  if (a.bottom > c.bottom) corpo.scrollTop += Math.min(a.bottom - c.bottom, a.top - c.top);
  else if (a.top < c.top) corpo.scrollTop -= c.top - a.top;
}

/**
 * Review rlb-2 (VIS-B1): `rolarAteVer` guardando a rolagem de antes; devolve quem a restaura (`manter` = fica onde está).
 * Exportada para o teste.
 */
export function rolarAteVerGuardando(corpo: Pick<HTMLElement, 'getBoundingClientRect' | 'scrollTop'>, alvo: Pick<HTMLElement, 'getBoundingClientRect'>) {
  const antes = corpo.scrollTop;
  rolarAteVer(corpo, alvo);
  return (manter: boolean) => {
    if (!manter) corpo.scrollTop = antes;
  };
}

function Cartao({ id, children, aoClicar }: { id: string; children: ReactNode; aoClicar?: () => void }) {
  const { t } = useTranslation();
  // Item 31: o cartão fica no lugar (esmaecido) e quem anda é a cópia na camada de arraste (DragOverlay, no portal do body).
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id, attributes: { roleDescription: t('kanban.papel') } });
  return (
    <div
      ref={setNodeRef}
      data-kanban-cartao={id}
      data-arrastando={isDragging || undefined}
      {...listeners}
      {...attributes}
      onClick={aoClicar}
      // Enter abre o cartão; Espaço (tratado pelo sensor de teclado do dnd-kit) pega e solta.
      onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
        listeners?.onKeyDown?.(e);
        if (e.key === 'Enter' && !isDragging && e.target === e.currentTarget && aoClicar) {
          e.preventDefault();
          aoClicar();
        }
      }}
      className={`cursor-grab rounded-lg border border-borda bg-sup-2 p-3 text-sm active:cursor-grabbing ${isDragging ? 'border-dashed opacity-40' : 'hover:border-tinta-3'}`}
    >
      {children}
    </div>
  );
}

/**
 * F7: kanban com arrastar (mouse, toque e teclado). A rolagem horizontal fica dentro do quadro.
 * U13: com `aoReordenarColunas`, a coluna também se arrasta pela alça do cabeçalho (Espaço e setas, ou Alt com seta); a
 * nova ordem aparece na hora e quem usa o quadro grava (e remonta o quadro se a gravação falhar).
 */
export function Kanban<T extends { id: string }>({
  colunas,
  itens,
  colunaDe,
  render,
  aoMover,
  aoClicar,
  rotulo,
  aoReordenarColunas,
  contagemNoTitulo,
  naAlturaDaJanela,
  acoesDoCartao,
}: {
  colunas: ColunaKanban[];
  itens: T[];
  colunaDe: (item: T) => string;
  render: (item: T) => ReactNode;
  aoMover: (item: T, colunaDestino: string) => void;
  aoClicar?: (item: T) => void;
  /** Nome legível do cartão para os anúncios de leitor de tela (padrão: o texto genérico de cartão). */
  rotulo?: (item: T) => string;
  /** U13: ids de todas as colunas na nova ordem; sem ele, as colunas ficam fixas. */
  aoReordenarColunas?: (ids: string[]) => void;
  /** Ajustes do CRM 27/10 (K7, K8), opt-in: o total entre parênteses ao lado do nome e o resumo numa etiqueta. */
  contagemNoTitulo?: boolean;
  /** Ajustes do CRM 27/10 (K10), opt-in: a coluna vai até a base da janela e os cartões rolam dentro dela (a página não rola). */
  naAlturaDaJanela?: boolean;
  /**
   * Ajustes do CRM 27/10 (K18, decisão 19), opt-in: botões no canto de cima do cartão, fora do elemento arrastável (um botão
   * nunca fica dentro de outro). Quem usa deixa o espaço no conteúdo do cartão.
   */
  acoesDoCartao?: (item: T) => ReactNode;
}) {
  const { t } = useTranslation();
  const raiz = useRef<HTMLDivElement>(null);
  // K10: o topo medido do quadro vira `--kanban-topo` (a altura da coluna desconta o cabeçalho da página).
  useLayoutEffect(() => {
    const el = raiz.current;
    if (!naAlturaDaJanela || !el) return;
    const medir = () => el.style.setProperty('--kanban-topo', `${Math.ceil(el.getBoundingClientRect().top + window.scrollY + (el.closest('[data-rolagem-conteudo]')?.scrollTop ?? 0))}px`);
    medir();
    window.addEventListener('resize', medir);
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(medir);
    if (el.parentElement) ro?.observe(el.parentElement);
    return () => {
      window.removeEventListener('resize', medir);
      ro?.disconnect();
    };
  }, [naAlturaDaJanela]);
  const sensores = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      keyboardCodes: {
        start: ['Space'],
        cancel: ['Escape'],
        end: ['Space', 'Enter'],
      },
      coordinateGetter: passoDoTeclado,
      scrollBehavior: movimentoReduzido() ? 'auto' : 'smooth',
    }),
  );
  // Item 31: o cartão em arraste (com a altura medida) e a coluna sob ele. Só muda ao pegar, ao trocar de coluna e ao soltar,
  // nunca a cada movimento do ponteiro (o quadro não se redesenha por pixel).
  const [ativo, setAtivo] = useState<{ id: string; altura: number } | null>(null);
  const [sobre, setSobre] = useState<string | null>(null);
  // Review rlb-2 (VIS-B1): a coluna em que o cartão foi solto (lida pela coluna quando a reserva sai).
  const soltura = useRef<string | null>(null);
  const lerSoltura = useCallback(() => soltura.current, []);
  const [local, setLocal] = useState<Record<string, string>>({});
  const [ordem, setOrdem] = useState<string[] | null>(null);
  // A ordem local vale enquanto as colunas forem as mesmas (a gravação devolve a mesma lista na ordem nova).
  const mesmas = ordem && ordem.length === colunas.length && colunas.every((c) => ordem.includes(c.id));
  const visiveis = mesmas ? ordem.map((id) => colunas.find((c) => c.id === id)!) : colunas;
  const coluna = (i: T) => local[i.id] ?? colunaDe(i);
  // Item 31: os cartões agrupados por coluna numa passada (antes, um filtro de todos os itens por coluna).
  const porColuna = useMemo(() => {
    const m = new Map<string, T[]>();
    for (const i of itens) {
      const c = local[i.id] ?? colunaDe(i);
      const l = m.get(c);
      if (l) l.push(i);
      else m.set(c, [i]);
    }
    return m;
  }, [itens, local, colunaDe]);
  const itemAtivo = ativo ? itens.find((i) => i.id === ativo.id) : undefined;
  const colunaDoAtivo = itemAtivo ? coluna(itemAtivo) : null;

  function reordenar(origem: string, destino: string) {
    const ids = visiveis.map((c) => c.id);
    const de = ids.indexOf(origem);
    const para = ids.indexOf(destino);
    if (de < 0 || para < 0 || de === para) return;
    ids.splice(para, 0, ...ids.splice(de, 1));
    setOrdem(ids);
    aoReordenarColunas?.(ids);
  }
  function aoPegar(e: DragStartEvent) {
    soltura.current = null;
    if (idDaColuna(e.active.id)) return;
    // A altura vem do próprio cartão (o retângulo do dnd-kit ainda não foi medido no início do arraste).
    const id = String(e.active.id);
    const no = [...(raiz.current?.querySelectorAll<HTMLElement>('[data-kanban-cartao]') ?? [])].find((n) => n.dataset.kanbanCartao === id);
    setAtivo({ id, altura: Math.round(no?.getBoundingClientRect().height ?? e.active.rect.current.initial?.height ?? 0) });
  }
  function aoPassar(e: DragOverEvent) {
    const id = e.over ? String(e.over.id) : null;
    setSobre((s) => (s === id ? s : id));
  }
  function aoTerminar() {
    setAtivo(null);
    setSobre(null);
  }
  function aoSoltar(e: DragEndEvent) {
    const destino = e.over?.id as string | undefined;
    const colunaArrastada = idDaColuna(e.active.id);
    soltura.current = !colunaArrastada && destino ? destino : null;
    aoTerminar();
    if (colunaArrastada) {
      if (destino) reordenar(colunaArrastada, destino);
      return;
    }
    const item = itens.find((i) => i.id === e.active.id);
    if (!item || !destino || destino === coluna(item)) return;
    setLocal((l) => ({ ...l, [item.id]: destino }));
    aoMover(item, destino);
  }
  const tituloDe = (id: string | number) => {
    const item = itens.find((i) => i.id === id);
    return (item && rotulo?.(item)) || t('kanban.cartao');
  };
  const colunaNome = (id?: string | number) => colunas.find((c) => c.id === id)?.titulo ?? '';
  // Anúncios no idioma do app, com o título do cartão (ou da coluna) e o nome da coluna (nunca o UUID).
  const anuncios: Announcements = {
    onDragStart: ({ active }) => {
      const c = idDaColuna(active.id);
      return c ? t('kanban.colunaInicio', { coluna: colunaNome(c) }) : t('kanban.inicio', { titulo: tituloDe(active.id) });
    },
    onDragOver: ({ active, over }) => {
      const c = idDaColuna(active.id);
      if (!over) return undefined;
      return c
        ? t('kanban.colunaSobre', {
            coluna: colunaNome(c),
            destino: colunaNome(over.id),
          })
        : t('kanban.sobre', {
            titulo: tituloDe(active.id),
            coluna: colunaNome(over.id),
          });
    },
    onDragEnd: ({ active, over }) => {
      const c = idDaColuna(active.id);
      if (c)
        return over && over.id !== c
          ? t('kanban.colunaSolta', {
              coluna: colunaNome(c),
              destino: colunaNome(over.id),
            })
          : t('kanban.colunaCancelada', { coluna: colunaNome(c) });
      return over
        ? t('kanban.solto', {
            titulo: tituloDe(active.id),
            coluna: colunaNome(over.id),
          })
        : t('kanban.soltoFora', { titulo: tituloDe(active.id) });
    },
    onDragCancel: ({ active }) => {
      const c = idDaColuna(active.id);
      return c ? t('kanban.colunaCancelada', { coluna: colunaNome(c) }) : t('kanban.cancelado', { titulo: tituloDe(active.id) });
    },
  };
  return (
    <DndContext
      sensors={sensores}
      collisionDetection={colisao}
      // Item 31: rola o quadro sozinho perto da borda (lateral um pouco mais cedo que a vertical).
      autoScroll={{ threshold: { x: 0.15, y: 0.2 }, acceleration: 12 }}
      onDragStart={aoPegar}
      onDragOver={aoPassar}
      onDragEnd={aoSoltar}
      onDragCancel={aoTerminar}
      accessibility={{
        announcements: anuncios,
        screenReaderInstructions: {
          draggable: aoReordenarColunas ? `${t('kanban.instrucoes')} ${t('kanban.instrucoesColuna')}` : t('kanban.instrucoes'),
        },
      }}
    >
      {/* Review r16 visual B7: título de nível 2 (só para leitor de tela) antes dos h3 das colunas: depois do h1 da seção, o
          Kanban de Tarefas pulava do h1 para o h3 (heading-order). */}
      <h2 className="sr-only">{t('kanban.colunas')}</h2>
      <RolagemHorizontal classeInterna="px-0 pb-2">
        <div ref={raiz} className="flex gap-3">
          {visiveis.map((c, n) => {
            const daColuna = porColuna.get(c.id) ?? [];
            return (
              <Coluna
                key={c.id}
                coluna={c}
                total={daColuna.length}
                reserva={ativo && sobre === c.id && sobre !== colunaDoAtivo ? ativo.altura : undefined}
                soltura={lerSoltura}
                arrastavel={!!aoReordenarColunas}
                contagemNoTitulo={contagemNoTitulo}
                naAlturaDaJanela={naAlturaDaJanela}
                aoMoverPeloTeclado={(passo) => {
                  const vizinha = visiveis[n + passo];
                  if (vizinha) reordenar(c.id, vizinha.id);
                }}
              >
                {daColuna.map((i) =>
                  acoesDoCartao ? (
                    <div key={i.id} className="relative">
                      <Cartao id={i.id} aoClicar={aoClicar ? () => aoClicar(i) : undefined}>
                        {render(i)}
                      </Cartao>
                      <div className="absolute top-1.5 right-1.5 flex items-center gap-0.5">{acoesDoCartao(i)}</div>
                    </div>
                  ) : (
                    <Cartao key={i.id} id={i.id} aoClicar={aoClicar ? () => aoClicar(i) : undefined}>
                      {render(i)}
                    </Cartao>
                  ),
                )}
              </Coluna>
            );
          })}
        </div>
      </RolagemHorizontal>
      {/* Item 31: a camada de arraste fica no body, acima das colunas, da barra e da faixa (z-50, a camada do Modal e do Drawer,
          abaixo dos avisos z-60 que dão a recusa). A cópia é só visual: o foco e os anúncios seguem no cartão de origem. */}
      {typeof document !== 'undefined' &&
        createPortal(
          <DragOverlay zIndex={50} dropAnimation={movimentoReduzido() ? null : { duration: 180, easing: 'cubic-bezier(0.2, 0, 0, 1)' }}>
            {itemAtivo ? (
              <div aria-hidden data-kanban-camada="" className="cursor-grabbing rounded-lg border border-acento-forte bg-sup-2 p-3 text-sm shadow-xl">
                {render(itemAtivo)}
              </div>
            ) : null}
          </DragOverlay>,
          document.body,
        )}
    </DndContext>
  );
}
